<!--
  A view's loading screen: the Changeling mark, what is loading, and the
  steps of the load with the state each is in (loading/steps.ts). A step
  shows a bar only where it has a real fraction. A load that stopped shows
  why, with the failure notice every failed load uses, and the mark holds
  still.
-->
<script lang="ts">
  import type { Retrying } from '../api/scheduler';
  import { inDuration } from '../api/rateLimit';
  import LoadError from '../components/LoadError.svelte';
  import Mark from './Mark.svelte';
  import type { LoadView, Step, StepStatus } from './steps';

  interface Props {
    view: LoadView;
    /** Try the stopped step again. */
    onretry?: () => void;
    /** The next retry of a failed request the scheduler is waiting to send again, or null. */
    retrying?: Retrying | null;
    /** For a part of a page under its own header (the Files view): at the top of its space, not centred in a window's height. */
    compact?: boolean;
  }
  let { view, onretry, retrying = null, compact = false }: Props = $props();

  const SAID: Record<StepStatus, string> = { waiting: 'waiting', active: 'in progress', done: 'done', held: 'waiting to try again', failed: 'failed' };

  const stopped = $derived(view.stopped);
  const held = $derived(view.steps.find((s) => s.id === stopped?.step)?.status === 'held');
  const needed = $derived(view.steps.filter((s) => s.needed));
  const later = $derived(view.steps.filter((s) => !s.needed));
  const uid = $props.id();

  // The countdown to a retry: read again each second while one waits.
  let now = $state(Date.now());
  $effect(() => {
    if (!retrying) return;
    now = Date.now();
    const tick = setInterval(() => (now = Date.now()), 1000);
    return () => clearInterval(tick);
  });
</script>

{#snippet row(s: Step)}
  <li class="step" data-step={s.id} data-status={s.status}>
    <span class="glyph" aria-hidden="true"></span>
    <span class="label">{s.label}<span class="sr-only">: {SAID[s.status]}</span></span>
    {#if s.detail}<span class="detail">{s.detail}</span>{/if}
    {#if s.progress}
      <span class="bar" role="progressbar" aria-label={s.label} aria-valuemin="0" aria-valuemax={s.progress.total} aria-valuenow={s.progress.done}
        ><span style:transform="scaleX({s.progress.done / s.progress.total})"></span></span
      >
    {/if}
  </li>
{/snippet}

<div class="loader" class:compact data-loader data-state={stopped ? (held ? 'held' : 'failed') : 'loading'} aria-busy={!stopped}>
  <div class="head">
    <Mark still={!!stopped} size={compact ? 44 : 56} />
    <div class="what">
      <p class="eyebrow">{stopped ? (held ? 'Waiting to load' : "Couldn't load") : 'Loading'}</p>
      <h2 class="title">{view.title}</h2>
    </div>
  </div>
  <ol class="steps" aria-label="What is loading">
    {#each needed as s (s.id)}{@render row(s)}{/each}
  </ol>
  {#if later.length}
    <div class="later">
      <p class="eyebrow" id="{uid}-later">Then, once it opens</p>
      <ol class="steps" aria-labelledby="{uid}-later">
        {#each later as s (s.id)}{@render row(s)}{/each}
      </ol>
    </div>
  {/if}
  {#if retrying && !stopped}
    <p class="note" data-retrying>
      A request failed.
      <span aria-live="off">{retrying.at <= now ? 'Trying it again now…' : `Trying it again in ${inDuration(retrying.at - now)}`} (try {retrying.attempt}).</span>
    </p>
  {/if}
  {#if stopped}
    <div class="why"><LoadError failure={stopped.failure} retry={stopped.retry} what={stopped.what} {onretry} /></div>
  {/if}
</div>

<style>
  .loader {
    display: grid;
    justify-items: center;
    align-content: center;
    gap: 22px;
    width: 100%;
    min-height: min(68vh, 620px);
    padding: 24px 16px;
  }
  .loader.compact {
    min-height: 0;
    padding: 36px 16px 48px;
  }
  .head {
    display: grid;
    justify-items: center;
    gap: 14px;
    text-align: center;
    min-width: 0;
    max-width: 100%;
  }
  .what {
    display: grid;
    gap: 2px;
    min-width: 0;
  }
  .title {
    font-size: 20px;
    line-height: 1.3;
    letter-spacing: -0.02em;
    font-weight: 650;
    overflow-wrap: anywhere;
  }
  .steps {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 7px;
    width: min(340px, 100%);
    font-size: 14px;
    line-height: 1.4;
  }
  .later {
    display: grid;
    gap: 8px;
    width: min(340px, 100%);
    padding-top: 14px;
    border-top: 1px solid var(--line);
    margin-top: -6px;
  }
  .later .step {
    font-size: 13px;
  }
  .step {
    display: grid;
    grid-template-columns: 16px minmax(0, 1fr) auto;
    align-items: center;
    column-gap: 10px;
    color: var(--muted);
  }
  .step[data-status='active'],
  .step[data-status='held'],
  .step[data-status='failed'] {
    color: var(--fg);
  }
  .label {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .detail {
    font: 12px ui-monospace, SFMono-Regular, Consolas, monospace;
    color: var(--muted);
    white-space: nowrap;
  }

  /* The state of a step: a ring (waiting), a filled dot breathing (active), a tick (done), a bar (held), a cross (failed). */
  .glyph {
    width: 14px;
    height: 14px;
    justify-self: center;
    border-radius: 50%;
    border: 1.5px solid var(--line);
    position: relative;
  }
  [data-status='active'] .glyph {
    border-color: var(--accent);
  }
  [data-status='active'] .glyph::after {
    content: '';
    position: absolute;
    inset: 2.5px;
    border-radius: 50%;
    background: var(--accent);
    animation: breathe 1.4s ease-in-out infinite;
  }
  @keyframes breathe {
    0%,
    100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.35;
      transform: scale(0.7);
    }
  }
  [data-status='done'] .glyph {
    border-color: var(--good);
    background: var(--good);
  }
  [data-status='done'] .glyph::after {
    content: '';
    position: absolute;
    left: 3.5px;
    top: 1px;
    width: 3px;
    height: 6.5px;
    border: solid var(--surface);
    border-width: 0 1.5px 1.5px 0;
    transform: rotate(45deg);
  }
  [data-status='held'] .glyph {
    border-color: var(--warn);
  }
  [data-status='held'] .glyph::after {
    content: '';
    position: absolute;
    inset: 2.5px 4.5px;
    border: solid var(--warn);
    border-width: 0 1.5px;
  }
  [data-status='failed'] .glyph {
    border-color: var(--bad);
  }
  [data-status='failed'] .glyph::before,
  [data-status='failed'] .glyph::after {
    content: '';
    position: absolute;
    left: 5px;
    top: 2px;
    width: 1.5px;
    height: 7.5px;
    background: var(--bad);
    transform: rotate(45deg);
  }
  [data-status='failed'] .glyph::after {
    transform: rotate(-45deg);
  }

  .bar {
    grid-column: 2 / -1;
    height: 3px;
    margin-top: 5px;
    border-radius: 3px;
    background: var(--line);
    overflow: hidden;
  }
  .bar span {
    display: block;
    height: 100%;
    background: var(--accent);
    transform-origin: left;
    transition: transform 0.2s ease-out;
  }

  .note {
    width: min(340px, 100%);
    font-size: 13px;
    color: var(--muted);
  }
  .why {
    width: min(560px, 100%);
  }
  @media (prefers-reduced-motion: reduce) {
    [data-status='active'] .glyph::after {
      animation: none;
    }
    .bar span {
      transition: none;
    }
  }
</style>
